import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {
    HalContext,
    HalImage,
    HalImageDataType,
    HalImageDescriptor,
    HalImageRef,
    HalImageView,
    HalImageViewRef,
    HalPaintable,
    HalRef,
    isHalImageDescriptor,
} from "@cm/hal"
import {ImageOpContextBase} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-base"
import {Descriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {channelLayoutByNumChannels} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {ReductionHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/reduction-helper-hal"
import {MipmapHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/mipmap-helper-hal"
import {ArithmeticHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/arithmetic-helper-hal"
import {Box2Like, Vector2Like} from "@cm/math"
import {PainterCacheHal, PainterType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/painter-cache-hal"
import {assertNever} from "@cm/utils"
import {ResizeHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/resize-helper-hal"
import {ImageOpDataObjectManager} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object-manager"
import {DownloadFormat, ImageOpDataObject} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object"
import {AddressMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {isHalDescriptorCompatible} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/hal-utils"
import {isHalImageVirtual} from "@cm/hal/hal-image/utils"
import {ImageViewArgs} from "@cm/hal/hal-image/index"
import {HalPainterImageCompositorRef, HalPainterPrimitiveRef} from "@cm/hal/hal-ref"
import {HalGeometryRef} from "@cm/hal/hal-geometry/index"
import {loggerFactory} from "@cm/browser-log"
import {ConvolveHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/convolve-helper-hal"
import {HalImageSourceData} from "@cm/hal/hal-image/types"

const logger = loggerFactory.getLogger("image-op-system")

export class ImageOpContextHal extends ImageOpContextBase {
    readonly type = "hal"

    constructor(
        readonly painterCache: PainterCacheHal,
        readonly halContext: HalContext,
        texturesApi: TexturesApiService,
    ) {
        super(texturesApi)
        logger.trace("Creating ImageOp helpers")
        logger.trace("  - MipmapHelper")
        const mipmapHelper = new MipmapHelperHal(this.halContext)
        logger.trace("  - ReductionHelper (sum)")
        const reductionHelperSum = new ReductionHelperHal(this.halContext, {operator: "sum"})
        logger.trace("  - ResizeHelper")
        const resizeHelper = new ResizeHelperHal(this.halContext)
        logger.trace("  - ConvolveHelper")
        const convolveHelper = new ConvolveHelperHal(this.halContext)
        logger.trace("  - ArithmeticHelper")
        const arithmeticHelper = new ArithmeticHelperHal(this.halContext)
        this._helpers = {
            mipmapHelper,
            reductionHelperSum,
            resizeHelper,
            convolveHelper,
            arithmeticHelper,
        }
    }

    override dispose(): void {
        if (!this._helpers) {
            throw new Error("ImageOpContextHal: Already disposed")
        }
        this._helpers.mipmapHelper.dispose()
        this._helpers.reductionHelperSum.dispose()
        this._helpers.resizeHelper.dispose()
        this._helpers.convolveHelper.dispose()
        this._helpers.arithmeticHelper.dispose()
        this._helpers = null

        this._dataObjectManager = null

        super.dispose()
    }

    set dataObjectManager(dataObjectManager: ImageOpDataObjectManager) {
        this._dataObjectManager = dataObjectManager
    }

    get dataObjectManager(): ImageOpDataObjectManager {
        if (!this._dataObjectManager) {
            throw new Error("DataObjectManager not set")
        }
        return this._dataObjectManager
    }

    get pageSize() {
        return this.halContext.descriptor.atlasPageSize
    }

    createImageVirtual(descriptor: Descriptor | HalImageDescriptor, pagePriority?: number) {
        return this.halContext.createVirtualImage(
            {
                width: descriptor.width,
                height: descriptor.height,
                channelLayout: isHalImageDescriptor(descriptor) ? descriptor.channelLayout : channelLayoutByNumChannels(descriptor.channels),
                dataType: "float32",
                numMipLevels: 0, // full mip chain
            },
            pagePriority,
        )
    }

    createImagePhysical(descriptor: Descriptor | HalImageDescriptor, options?: {dataType?: HalImageDataType; initialData?: HalImageSourceData}) {
        const image = this.halContext.createPhysicalImage({
            width: descriptor.width,
            height: descriptor.height,
            channelLayout: isHalImageDescriptor(descriptor) ? descriptor.channelLayout : channelLayoutByNumChannels(descriptor.channels),
            dataType: options?.dataType ?? (isHalImageDescriptor(descriptor) ? descriptor.dataType : "float32"),
            numMipLevels: 1, // no mipmaps
        })
        if (options?.initialData) {
            image.ref.writeImageData({
                sourceData: options.initialData,
            })
        }
        return image
    }

    createImageView(args: {image: HalImageRef} & ImageViewArgs): HalImageViewRef {
        const {image, ...viewArgs} = args
        image.addRef()
        const halImageView = image.ref.createView({
            ...viewArgs,
        })
        return new HalRef<HalImageView>(halImageView, () => image.release())
    }

    createPainter(painterType: "primitive", code: string): HalPainterPrimitiveRef
    createPainter(painterType: "compositor", code: string): HalPainterImageCompositorRef

    createPainter(painterType: PainterType, code: string) {
        switch (painterType) {
            case "primitive": {
                return this.painterCache.getOrCreatePainter(painterType, code)
            }
            case "compositor": {
                return this.painterCache.getOrCreatePainter(painterType, code)
            }
            default:
                assertNever(painterType)
        }
    }

    createGeometry(): HalGeometryRef {
        return this.halContext.createGeometry()
    }

    async loadDataObjectImage(args: {dataObjectId: string; downloadFormat: DownloadFormat}): Promise<ImageOpDataObject> {
        return this.dataObjectManager.loadDataObjectImage(args)
    }

    blit(args: {
        sourceImage: HalImage
        sourceRegion?: Box2Like
        sourceMipLevel?: number
        targetImage: HalPaintable
        targetOffset?: Vector2Like
        targetMipLevel?: number
        addressMode?: AddressMode
    }): void {
        this.halContext.blit(args)
    }

    async copyImageData(args: {targetImage: HalImage; sourceImage: HalImage; region?: Box2Like; mipLevel?: number}) {
        if (!isHalDescriptorCompatible(args.targetImage.descriptor, args.sourceImage.descriptor)) {
            throw new Error("Target and source image descriptors do not match")
        }
        const region = args.region ?? {x: 0, y: 0, width: args.sourceImage.width, height: args.sourceImage.height}
        const mipLevel = args.mipLevel ?? 0
        // TODO we don't use writeImageData in all cases because it has still problems with wrapping views !
        // TODO what about views on virtual images ?
        if (isHalImageVirtual(args.targetImage)) {
            // const wrappedSourceRegions = applyWrapToRegion(args.sourceImage.descriptor, region, mipLevel)
            // await Promise.all(
            //     wrappedSourceRegions.map((region) =>
            //         args.targetImage.writeImageData({
            //             sourceData: args.sourceImage,
            //             sourceRegion: region,
            //             sourceMipLevel: mipLevel,
            //             targetOffset: region,
            //             targetMipLevel: mipLevel,
            //         }),
            //     ),
            // )
            await args.targetImage.writeImageData({
                sourceData: args.sourceImage,
                sourceRegion: region,
                sourceMipLevel: mipLevel,
                targetOffset: region,
                targetMipLevel: mipLevel,
            })
        } else {
            this.blit({
                sourceImage: args.sourceImage,
                sourceRegion: region,
                sourceMipLevel: mipLevel,
                targetImage: args.targetImage,
                targetOffset: region,
                targetMipLevel: mipLevel,
                addressMode: "wrap",
            })
        }
    }

    get mipmapHelper(): MipmapHelperHal {
        if (!this._helpers) {
            throw new Error("ImageOpContextHal: Already disposed")
        }
        return this._helpers.mipmapHelper
    }

    get reductionHelperSum(): ReductionHelperHal {
        if (!this._helpers) {
            throw new Error("ImageOpContextHal: Already disposed")
        }
        return this._helpers.reductionHelperSum
    }

    get resizeHelper(): ResizeHelperHal {
        if (!this._helpers) {
            throw new Error("ImageOpContextHal: Already disposed")
        }
        return this._helpers.resizeHelper
    }

    get convolveHelper(): ConvolveHelperHal {
        if (!this._helpers) {
            throw new Error("ImageOpContextHal: Already disposed")
        }
        return this._helpers.convolveHelper
    }

    get arithmeticHelper(): ArithmeticHelperHal {
        if (!this._helpers) {
            throw new Error("ImageOpContextHal: Already disposed")
        }
        return this._helpers.arithmeticHelper
    }

    private _helpers: Helpers | null
    private _dataObjectManager: ImageOpDataObjectManager | null = null
}

type Helpers = {
    mipmapHelper: MipmapHelperHal
    reductionHelperSum: ReductionHelperHal
    resizeHelper: ResizeHelperHal
    convolveHelper: ConvolveHelperHal
    arithmeticHelper: ArithmeticHelperHal
}
